import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonList,
  IonRow,
  IonToggle,
  IonToolbar,
} from '@ionic/react'
import React from 'react'
import SingleSelect from '../../../../../components/singleSelect/SingleSelect.component'
import { useTranslation } from 'react-i18next'
import { getGlassgowCommaScoreSelector } from '../selectors/glassgowCommaScore.selector'
import { useDispatch, useSelector } from 'react-redux'
import { IOption, IPNNVOption } from '../../../../../common/common.types'
import {
  setEyeResponse,
  setVerbalResponse,
  setMotorResponse,
  setQualifier,
  saveGlassgowCommaScore,
  setEyeReasonEnabled,
  setVerbalReasonEnabled,
  setMotorReasonEnabled,
  resetGlassgowCommaScoreData,
  setTotalReasonEnabled,
  setGlassgowCommaScore,
  setEyePNNV,
  setVerbalPNNV,
  setMotorPNNV,
  setTotalPNNV,
  setQualifierPNNV,
} from '../store/glassgowCommaScore.actions'
import './GlassgowCommaScore.content.css'
import MultiSelect from '../../../../../components/multiSelect/MultiSelect.component'
import { getGCSOptionsSelector } from '../../../../options/selectors/gcsOptions.selector'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../../../components/tabs/Tabs.constants'
import { getServiceConfigSelector } from '../../../../configurations/selectors/serviceConfig.selector'
import { IServiceConfig } from '../../../../configurations/store/configurations.types'
import { getAllPNNVOptionsSelector } from '../../../../options/selectors'

const GlassgowCommaScoreContent: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const glassgowCommaScoreState = useSelector(getGlassgowCommaScoreSelector)
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { PR_PERTNEG } = serviceConfigData
  const {
    gcsEye,
    gcsEyePNNV,
    gcsVerbal,
    gcsVerbalPNNV,
    gcsMotor,
    gcsMotorPNNV,
    qualifier,
    qualifierPNNV,
    _isGcsEyePNNVEnabled,
    _isGcsMotorPNNVEnabled,
    _isGcsVerbalPNNVEnabled,
    gcs,
    gcsPNNV,
    _isGcsPNNVEnabled,
  } = glassgowCommaScoreState
  const gcsTotal = gcsEyePNNV || gcsMotorPNNV || gcsVerbalPNNV || gcsPNNV ? null : gcs || 'N/A'
  const gcsOptionsData = useSelector(getGCSOptionsSelector)
  const { gcsQualifierOptions, gcsEyeResponseOptions, gcsMotorResponseOptions, gcsVerbalResponseOptions } =
    gcsOptionsData

  const allPNNVOptions = useSelector(getAllPNNVOptionsSelector)
  const gcsEyeNegativeOptions = allPNNVOptions?.['eVitals.19']
  const gcsVerbalNegativeOptions = allPNNVOptions?.['eVitals.20']
  const gcsMotorNegativeOptions = allPNNVOptions?.['eVitals.21']
  const gcsTotalNegativeOptions = allPNNVOptions?.['eVitals.23']

  const saveGCS = () => {
    dispatch(saveGlassgowCommaScore())
  }

  const checkGCSNegativeTotal = (option: IPNNVOption | null) => {
    if (!gcsPNNV && option) {
      const gcsData: any = gcsTotalNegativeOptions.find((optionObj) => optionObj.name === option?.name)
      const GCSNegativePNNVOption = {
        id: gcsData.id,
        name: gcsData.name,
        pn: gcsData.isPN && gcsData.nemsisCode != null ? gcsData.nemsisCode.toString() : '',
        nv: gcsData.isNV && gcsData.nemsisCode != null ? gcsData.nemsisCode.toString() : '',
      }
      dispatch(setTotalReasonEnabled(true))
      setTotalReasonPNNVOnChange(GCSNegativePNNVOption || null)
    }
    saveGCS()
  }

  const setEyeResponseOnChange = (option: IOption | null) => {
    dispatch(setEyeResponse(option))
    dispatch(setEyePNNV(null))
    saveGCS()
  }

  const setEyePNNVOnChange = (option: IPNNVOption | null) => {
    checkGCSNegativeTotal(option)
    dispatch(setEyePNNV(option))
    dispatch(setEyeResponse(null))
    saveGCS()
  }

  const setVerbalResponseOnChange = (option: IOption | null) => {
    dispatch(setVerbalResponse(option))
    dispatch(setVerbalPNNV(null))
    saveGCS()
  }

  const setVerbalPNNVOnChange = (option: IPNNVOption | null) => {
    checkGCSNegativeTotal(option)
    dispatch(setVerbalPNNV(option))
    dispatch(setVerbalResponse(null))
    saveGCS()
  }

  const setMotorResponseOnChange = (option: IOption | null) => {
    dispatch(setMotorResponse(option))
    dispatch(setMotorPNNV(null))
    saveGCS()
  }

  const setMotorPNNVOnChange = (option: IPNNVOption | null) => {
    checkGCSNegativeTotal(option)
    dispatch(setMotorPNNV(option))
    dispatch(setMotorResponse(null))
    saveGCS()
  }

  const setTotalReasonPNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setTotalPNNV(option))
    setTotalResponseOnChange(null)
    saveGCS()
  }

  const setEyeReasonEnabledonChange = () => {
    dispatch(setEyeReasonEnabled(!_isGcsEyePNNVEnabled))
    if (!_isGcsEyePNNVEnabled) {
      setEyeResponseOnChange(null)
    } else {
      setEyePNNVOnChange(null)
    }
  }

  const setVerbalReasonEnabledonChange = () => {
    dispatch(setVerbalReasonEnabled(!_isGcsVerbalPNNVEnabled))
    if (!_isGcsVerbalPNNVEnabled) {
      setVerbalResponseOnChange(null)
    } else {
      setVerbalPNNVOnChange(null)
    }
  }

  const setMotorReasonEnabledonChange = () => {
    dispatch(setMotorReasonEnabled(!_isGcsMotorPNNVEnabled))
    if (!_isGcsMotorPNNVEnabled) {
      setMotorResponseOnChange(null)
    } else {
      setMotorPNNVOnChange(null)
    }
  }

  const setTotalResponseOnChange = (option: IOption | null) => {
    dispatch(setGlassgowCommaScore(option))
    saveGCS()
  }

  const setTotalReasonEnabledonChange = () => {
    dispatch(setTotalReasonEnabled(!_isGcsPNNVEnabled))
    if (!_isGcsPNNVEnabled) {
      setTotalResponseOnChange(null)
    } else {
      setTotalReasonPNNVOnChange(null)
    }
  }

  const resetGcsData = () => {
    dispatch(resetGlassgowCommaScoreData())
    saveGCS()
  }

  return (
    <>
      <IonContent className='ion-margin-bottom' fullscreen>
        <IonList>
          <IonGrid className='ion-no-padding'>
            <IonRow className='mt-4 mb-4'>
              <IonRow className='w-100 flex ion-justify-content-between'>
                <div className='m-8-10'>
                  <IonLabel>{t('neuro.glassgowCommaScore.gcs')} </IonLabel>
                  {gcsTotal && <IonLabel className='custom-number-label'> {gcsTotal}</IonLabel>}
                </div>
                {Boolean(PR_PERTNEG) && (
                  <IonItem lines='none'>
                    <IonToggle
                      onClick={setTotalReasonEnabledonChange}
                      color='zoll-blue-secondary'
                      checked={_isGcsPNNVEnabled}
                    />
                    <IonLabel className='m-5' color='medium'>
                      {t('neuro.glassgowCommaScore.NotDone')}
                    </IonLabel>
                  </IonItem>
                )}
              </IonRow>

              {_isGcsPNNVEnabled && (
                <>
                  <SingleSelect
                    dataTestID='TotalReason'
                    options={gcsTotalNegativeOptions}
                    listHeader={t('neuro.glassgowCommaScore.ReasonNotDone')}
                    label={t('neuro.glassgowCommaScore.ReasonNotDone')}
                    pnnvAction={setTotalReasonPNNVOnChange}
                    action={() => {
                      /**/
                    }}
                    selectedPNNVOption={gcsPNNV}
                    showPNNVAsNormals={true}
                  />
                  <hr className='field-divider' />
                </>
              )}
            </IonRow>
            <IonItemDivider color='light' className='grid-item-divider-container'></IonItemDivider>
            <IonRow>
              <IonRow className='w-100 flex ion-justify-content-between'>
                <IonLabel className='m-8-10' color='medium'>
                  {t('neuro.glassgowCommaScore.gcsEye')}
                </IonLabel>
                {Boolean(PR_PERTNEG) && (
                  <IonItem lines='none'>
                    <IonToggle
                      onClick={setEyeReasonEnabledonChange}
                      color='zoll-blue-secondary'
                      checked={_isGcsEyePNNVEnabled}
                    />
                    <IonLabel className='m-5' color='medium'>
                      {t('neuro.glassgowCommaScore.NotDone')}
                    </IonLabel>
                  </IonItem>
                )}
              </IonRow>
              <IonCol className='picklist'>
                <SingleSelect
                  dataTestID={_isGcsEyePNNVEnabled ? 'gcsEye2' : 'gcsEye'}
                  options={_isGcsEyePNNVEnabled ? gcsEyeNegativeOptions : gcsEyeResponseOptions}
                  selectedOption={_isGcsEyePNNVEnabled ? gcsEyePNNV : gcsEye}
                  listHeader={
                    _isGcsEyePNNVEnabled
                      ? t('neuro.glassgowCommaScore.ReasonNotDone')
                      : t('neuro.glassgowCommaScore.Response')
                  }
                  label={
                    _isGcsEyePNNVEnabled
                      ? t('neuro.glassgowCommaScore.ReasonNotDone')
                      : t('neuro.glassgowCommaScore.Response')
                  }
                  action={setEyeResponseOnChange}
                  {...(_isGcsEyePNNVEnabled && { pnnvAction: setEyePNNVOnChange })}
                  {...(_isGcsEyePNNVEnabled && { selectedPNNVOption: gcsEyePNNV })}
                  {...(!_isGcsEyePNNVEnabled && { showSelectedOptionId: 'prefixScore' })}
                  showPNNVAsNormals={_isGcsEyePNNVEnabled}
                />
                <hr className='field-divider' />
              </IonCol>
            </IonRow>
            <IonItemDivider color='light' className='grid-item-divider-container'></IonItemDivider>
            <IonRow>
              <IonRow className='w-100 flex ion-justify-content-between'>
                <IonLabel className='m-8-10' color='medium'>
                  {t('neuro.glassgowCommaScore.gcsVerbal')}
                </IonLabel>
                {Boolean(PR_PERTNEG) && (
                  <IonItem lines='none'>
                    <IonToggle
                      onClick={setVerbalReasonEnabledonChange}
                      color='zoll-blue-secondary'
                      checked={_isGcsVerbalPNNVEnabled}
                    />
                    <IonLabel className='m-5' color='medium'>
                      {t('neuro.glassgowCommaScore.NotDone')}
                    </IonLabel>
                  </IonItem>
                )}
              </IonRow>
              <IonCol className='picklist'>
                <SingleSelect
                  dataTestID={_isGcsVerbalPNNVEnabled ? 'gcsVerbal2' : 'gcsVerbal'}
                  options={_isGcsVerbalPNNVEnabled ? gcsVerbalNegativeOptions : gcsVerbalResponseOptions}
                  selectedOption={_isGcsVerbalPNNVEnabled ? gcsVerbalPNNV : gcsVerbal}
                  listHeader={
                    _isGcsVerbalPNNVEnabled
                      ? t('neuro.glassgowCommaScore.ReasonNotDone')
                      : t('neuro.glassgowCommaScore.Response')
                  }
                  label={
                    _isGcsVerbalPNNVEnabled
                      ? t('neuro.glassgowCommaScore.ReasonNotDone')
                      : t('neuro.glassgowCommaScore.Response')
                  }
                  action={_isGcsVerbalPNNVEnabled ? setVerbalPNNVOnChange : setVerbalResponseOnChange}
                  {...(_isGcsVerbalPNNVEnabled && { pnnvAction: setVerbalPNNVOnChange })}
                  {...(_isGcsEyePNNVEnabled && { selectedPNNVOption: gcsVerbalPNNV })}
                  {...(!_isGcsVerbalPNNVEnabled && { showSelectedOptionId: 'prefixScore' })}
                  showPNNVAsNormals={_isGcsVerbalPNNVEnabled}
                />
                <hr className='field-divider' />
              </IonCol>
            </IonRow>
            <IonItemDivider color='light' className='grid-item-divider-container'></IonItemDivider>

            <IonRow>
              <IonRow className='w-100 flex ion-justify-content-between'>
                <IonLabel className='m-8-10' color='medium'>
                  {t('neuro.glassgowCommaScore.gcsMotor')}
                </IonLabel>
                {Boolean(PR_PERTNEG) && (
                  <IonItem lines='none'>
                    <IonToggle
                      onClick={setMotorReasonEnabledonChange}
                      color='zoll-blue-secondary'
                      checked={_isGcsMotorPNNVEnabled}
                    />
                    <IonLabel className='m-5' color='medium'>
                      {t('neuro.glassgowCommaScore.NotDone')}
                    </IonLabel>
                  </IonItem>
                )}
              </IonRow>

              <IonCol className='picklist'>
                <SingleSelect
                  dataTestID={_isGcsMotorPNNVEnabled ? 'gcsMotor2' : 'gcsMotor'}
                  options={_isGcsMotorPNNVEnabled ? gcsMotorNegativeOptions : gcsMotorResponseOptions}
                  selectedOption={_isGcsMotorPNNVEnabled ? gcsMotorPNNV : gcsMotor}
                  listHeader={
                    _isGcsMotorPNNVEnabled
                      ? t('neuro.glassgowCommaScore.ReasonNotDone')
                      : t('neuro.glassgowCommaScore.Response')
                  }
                  label={
                    _isGcsMotorPNNVEnabled
                      ? t('neuro.glassgowCommaScore.ReasonNotDone')
                      : t('neuro.glassgowCommaScore.Response')
                  }
                  action={_isGcsMotorPNNVEnabled ? setMotorPNNVOnChange : setMotorResponseOnChange}
                  {...(_isGcsMotorPNNVEnabled && { pnnvAction: setMotorPNNVOnChange })}
                  {...(_isGcsEyePNNVEnabled && { selectedPNNVOption: gcsMotorPNNV })}
                  {...(!_isGcsMotorPNNVEnabled && { showSelectedOptionId: 'prefixScore' })}
                  showPNNVAsNormals={_isGcsMotorPNNVEnabled}
                />
                <hr className='field-divider' />
              </IonCol>
            </IonRow>
            <IonRow className='mt-1'>
              <IonCol className='picklist ion-no-margin qualifier-multi-select' size='12'>
                <MultiSelect
                  dataTestID={t('neuro.glassgowCommaScore.PrGcsQualifier')}
                  listHeader={t('neuro.glassgowCommaScore.PrGcsQualifier')}
                  options={gcsQualifierOptions}
                  savedValues={qualifier}
                  action={setQualifier}
                  label={t('neuro.glassgowCommaScore.PrGcsQualifier')}
                  showChips={true}
                  savedPNNVValue={qualifierPNNV}
                  pnnvAction={setQualifierPNNV}
                />
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonList>
      </IonContent>
      <IonFooter>
        <IonToolbar color='light' className='p-10 '>
          <IonButtons className='btn-grps'>
            <IonButton className='btn btn-tablet' color='danger' fill='outline' expand='block' onClick={resetGcsData}>
              {t('common.buttons.Clear')}
            </IonButton>
            <IonButton
              className='btn btn-primary btn-tablet'
              expand='block'
              onClick={() => {
                history.push(TabConstants.INITIAL_ASSESSMENT)
              }}>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </>
  )
}

export default GlassgowCommaScoreContent
